import { useEffect, useMemo, useState } from 'react'
import {
  type ColumnDef,
  type OnChangeFn,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import {
  GitMerge,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  Clock3,
  TriangleAlert,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import { useCurrentOrganization } from '@/shared/hooks/api/organization'
import type {
  PackagingItemsOverviewExportQuery,
  PackagingItemsOverviewRow,
  PackagingItemsOverviewSortBy,
  PackagingItemsOverviewSortDir,
  PageInfo,
} from '@/shared/hooks/api/packagingItemsOverview'
import { downloadPackagingItemsOverviewExport } from '@/shared/hooks/api/packagingItemsOverview'
import {
  type NavigateFn,
  useTableUrlState,
} from '@/shared/hooks/use-table-url-state'
import { cn } from '@/shared/lib/utils'
import {
  parsePackagingItemsLastUpdatedFilter,
  type PackagingItemsLastUpdatedFilter,
} from '@/shared/packagingItemsLastUpdatedFilter'
import { Badge } from '@/components/ui/badge'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DataTableColumnHeader,
  DataTablePagination,
  DataTableToolbar,
} from '@/components/data-table'
import { ExportXlsxButton } from '@/features/packaging-suppliers/export/ExportXlsxButton'
import { buildPackagingExportFilename } from '@/features/packaging-suppliers/export/packagingExportFilename'
import { PackagingItemAuditCell } from './PackagingItemAuditCell'
import { PackagingItemChangeHistoryTimestampCell } from './PackagingItemChangeHistoryTimestampCell'
import { PackagingItemComplianceRedFlagsCell } from './PackagingItemComplianceRedFlagsCell'
import { PackagingItemHeavyMetalsAuditCell } from './PackagingItemHeavyMetalsAuditCell'
import { PackagingItemSkuCell } from './PackagingItemSkuCell'
import { PackagingItemSvhcAuditCell } from './PackagingItemSvhcAuditCell'
import { PackagingItemsLastUpdatedFacetedFilter } from './PackagingItemsLastUpdatedFacetedFilter'
import { PackagingItemsSupplierFilterCombobox } from './PackagingItemsSupplierFilterCombobox'
import {
  PACKAGING_ITEM_AUDIT_STATUS_VALUES,
  packagingItemAuditStatusLabelKey,
} from './packagingItemAuditFilter'
import { PACKAGING_ITEM_COMPLIANCE_RED_FLAG_VALUES } from './packagingItemComplianceRedFlagFilter'
import {
  PACKAGING_ITEMS_OVERVIEW_HEAVY_METALS_AUDIT_STATUS_FILTER_VALUES,
  PACKAGING_ITEMS_OVERVIEW_HEAVY_METALS_AUDIT_VERDICT_FILTER_VALUES,
  compareHeavyMetalsAuditVerdictFilterValues,
  packagingHeavyMetalsAuditVerdictLabelKey,
  packagingItemHeavyMetalsAuditStatusLabelKey,
  packagingItemMatchesHeavyMetalsAuditFilterValue,
} from './packagingItemHeavyMetalsAuditFilter'
import { packagingItemMatchesSupplierFilter } from './packagingItemSupplierFilterOptions'
import {
  PACKAGING_ITEMS_OVERVIEW_SVHC_AUDIT_STATUS_FILTER_VALUES,
  PACKAGING_ITEMS_OVERVIEW_SVHC_AUDIT_VERDICT_FILTER_VALUES,
  compareSvhcAuditVerdictFilterValues,
  packagingItemMatchesSvhcAuditFilterValue,
  packagingItemSvhcAuditStatusLabelKey,
  packagingSvhcAuditVerdictLabelKey,
} from './packagingItemSvhcAuditFilter'
import { redFlagLabelKey } from './packagingSupplierOverviewColumns'

const packagingItemsTableColumns = [
  { width: '13%' },
  { width: '7%' },
  { width: '11%' },
  { width: '6%' },
  { width: '9%' },
  { width: '7%' },
  { width: '9%' },
  { width: '9%' },
  { width: '9%' },
  { width: '9%' },
] as const

function packagingItemTimestampSortValue(
  raw: string | null | undefined
): number | undefined {
  if (raw == null || String(raw).trim() === '') {
    return undefined
  }
  const time = new Date(raw).getTime()
  if (Number.isNaN(time)) {
    return undefined
  }
  return time
}

function packagingItemStatusSortValue(
  row: PackagingItemsOverviewRow,
  isArtemAdmin: boolean
): number {
  if (isArtemAdmin && row.mergedIntoPackagingItem != null) {
    return 2
  }
  if (row.completedAt) {
    return 1
  }
  return 0
}

function PackagingItemsTableColGroup() {
  return (
    <colgroup>
      {packagingItemsTableColumns.map((column, index) => (
        <col key={index} style={{ width: column.width }} />
      ))}
    </colgroup>
  )
}

type PackagingItemsOverviewTableProps = {
  data: PackagingItemsOverviewRow[]
  pageInfo: PageInfo
  exportQuery: PackagingItemsOverviewExportQuery
  isFetching?: boolean
  search: Record<string, unknown>
  navigate: NavigateFn
  sortBy: PackagingItemsOverviewSortBy
  sortDir: PackagingItemsOverviewSortDir
}

export function PackagingItemsOverviewTable({
  data,
  pageInfo,
  exportQuery,
  isFetching = false,
  search,
  navigate,
  sortBy,
  sortDir,
}: PackagingItemsOverviewTableProps) {
  const { t } = useTranslation('packagingSuppliers')
  const { data: organization } = useCurrentOrganization()
  const { data: session } = useGetUserSession()
  const isArtemAdmin = session?.isArtemAdmin === true

  const [searchQuery, setSearchQuery] = useState(
    typeof search.q === 'string' ? search.q : ''
  )
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(
    (typeof search.q === 'string' ? search.q : '').trim()
  )
  const sorting = useMemo<SortingState>(
    () => [{ id: sortBy, desc: sortDir === 'desc' }],
    [sortBy, sortDir]
  )

  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater
    const primary = next[0]
    const nextSortBy =
      primary?.id != null && primary.id !== ''
        ? (primary.id as PackagingItemsOverviewSortBy)
        : 'name'
    const nextSortDir: PackagingItemsOverviewSortDir = primary?.desc
      ? 'desc'
      : 'asc'
    navigate({
      search: (previous) => ({
        ...previous,
        sortBy: nextSortBy === 'name' ? undefined : nextSortBy,
        sortDir: nextSortDir === 'asc' ? undefined : nextSortDir,
        page: undefined,
      }),
    })
  }

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim())
    }, 300)
    return () => {
      window.clearTimeout(handle)
    }
  }, [searchQuery])

  useEffect(() => {
    const currentQuery = typeof search.q === 'string' ? search.q.trim() : ''
    if (debouncedSearchQuery === currentQuery) {
      return
    }
    navigate({
      search: (previous) => {
        const next = { ...previous }
        if (debouncedSearchQuery === '') {
          delete next.q
        } else {
          next.q = debouncedSearchQuery
        }
        next.page = 1
        return next
      },
    })
  }, [debouncedSearchQuery, navigate, search.q])

  const {
    pagination,
    onPaginationChange,
    ensurePageInRange,
    columnFilters,
    onColumnFiltersChange,
  } = useTableUrlState({
    search,
    navigate,
    pagination: { defaultPage: 1, defaultPageSize: 20 },
    columnFilters: [
      { columnId: 'supplier', searchKey: 'supplier', type: 'string' },
      {
        columnId: 'complianceRedFlags',
        searchKey: 'complianceRedFlags',
        type: 'array',
      },
      { columnId: 'audit', searchKey: 'audit', type: 'array' },
      { columnId: 'lastUpdated', searchKey: 'lastUpdated', type: 'string' },
      {
        columnId: 'heavyMetalsAudit',
        searchKey: 'heavyMetalsAudit',
        type: 'array' as const,
      },
      {
        columnId: 'svhcAudit',
        searchKey: 'svhcAudit',
        type: 'array' as const,
      },
    ],
  })

  const supplierIdFromUrl = useMemo(() => {
    const found = columnFilters.find((filter) => filter.id === 'supplier')
    if (typeof found?.value === 'string') {
      const fromColumnFilters = found.value.trim()
      if (fromColumnFilters !== '') {
        return fromColumnFilters
      }
    }
    if (typeof search.supplier === 'string') {
      return search.supplier.trim()
    }
    return ''
  }, [columnFilters, search.supplier])

  const selectedSupplierName = useMemo(() => {
    const selectedRow = data.find((row) => row.supplierId === supplierIdFromUrl)
    return selectedRow?.supplierName
  }, [data, supplierIdFromUrl])

  const lastUpdatedFilter = useMemo(() => {
    const found = columnFilters.find((filter) => filter.id === 'lastUpdated')
    if (typeof found?.value === 'string') {
      return parsePackagingItemsLastUpdatedFilter(found.value)
    }
    return parsePackagingItemsLastUpdatedFilter(search.lastUpdated)
  }, [columnFilters, search.lastUpdated])

  const handleLastUpdatedFilterChange = (
    next: PackagingItemsLastUpdatedFilter | undefined
  ) => {
    onColumnFiltersChange((previous) => {
      const withoutLastUpdated = previous.filter(
        (filter) => filter.id !== 'lastUpdated'
      )
      if (next == null) {
        return withoutLastUpdated
      }
      return [...withoutLastUpdated, { id: 'lastUpdated', value: next }]
    })
  }

  const handleSupplierFilterChange = (supplierId: string | null) => {
    const next = supplierId?.trim() ?? ''
    onColumnFiltersChange((previous) => {
      const withoutSupplier = previous.filter(
        (filter) => filter.id !== 'supplier'
      )
      if (next === '') {
        return withoutSupplier
      }
      return [...withoutSupplier, { id: 'supplier', value: next }]
    })
  }

  const columns = useMemo<ColumnDef<PackagingItemsOverviewRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('list.columnName')} />
        ),
        enableHiding: false,
        cell: ({ row }) => {
          const mergedInto = row.original.mergedIntoPackagingItem
          return (
            <div className='flex min-w-0 items-center gap-1.5'>
              <Link
                to={`/packaging/suppliers/${row.original.supplierId}/items/${row.original.id}`}
                className='min-w-0 truncate text-foreground underline decoration-dotted underline-offset-2'
              >
                {row.original.name}
              </Link>
              {mergedInto != null && isArtemAdmin && (
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <button
                      type='button'
                      className='inline-flex shrink-0'
                      aria-label={t('packagingItems.mergedIntoTooltip', {
                        name: mergedInto.name,
                      })}
                    >
                      <Badge variant='secondary' className='gap-1 font-normal'>
                        <GitMerge className='size-3' aria-hidden />
                        {t('packagingItems.statusConsolidated')}
                      </Badge>
                    </button>
                  </HoverCardTrigger>
                  <HoverCardContent side='top' align='start'>
                    <p className='text-muted-foreground'>
                      {t('packagingItems.mergedIntoHoverLead')}
                    </p>
                    <Link
                      to={`/packaging/suppliers/${row.original.supplierId}/items/${mergedInto.id}`}
                      className='mt-1 block truncate font-medium text-foreground underline decoration-dotted underline-offset-2 hover:text-foreground'
                    >
                      {mergedInto.name}
                    </Link>
                  </HoverCardContent>
                </HoverCard>
              )}
            </div>
          )
        },
      },
      {
        accessorKey: 'sku',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('items.columnSku')} />
        ),
        enableHiding: false,
        cell: ({ row }) => {
          const sku = row.original.sku?.trim() ?? ''
          if (sku.length === 0) {
            return t('common:notAvailable')
          }
          return (
            <PackagingItemSkuCell
              sku={sku}
              skuInBomList={row.original.skuInBomList}
            />
          )
        },
      },
      {
        id: 'supplier',
        accessorFn: (row) => row.supplierName,
        filterFn: (row, _columnId, filterValue) =>
          packagingItemMatchesSupplierFilter(
            row.original.supplierId,
            filterValue
          ),
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('packedItems.columnSupplier')}
          />
        ),
        enableHiding: false,
        cell: ({ row }) => (
          <Link
            to={`/packaging/suppliers/${row.original.supplierId}`}
            className='block truncate text-foreground underline-offset-4 hover:underline'
          >
            {row.original.supplierName}
          </Link>
        ),
      },
      {
        id: 'status',
        accessorFn: (row) => packagingItemStatusSortValue(row, isArtemAdmin),
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('items.columnStatus')}
          />
        ),
        enableHiding: false,
        cell: ({ row }) => {
          if (isArtemAdmin && row.original.mergedIntoPackagingItem != null) {
            const label = t('packagingItems.statusConsolidated')
            return (
              <span className='inline-flex' title={label} aria-label={label}>
                <GitMerge
                  className='size-4 text-muted-foreground'
                  aria-hidden
                />
              </span>
            )
          }
          if (row.original.completedAt) {
            const label = t('packedItems.packagingItemComplete')
            return (
              <span className='inline-flex' title={label} aria-label={label}>
                <CircleCheck
                  className='size-4 text-green-600 dark:text-green-500'
                  aria-hidden
                />
              </span>
            )
          }
          const label = t('packedItems.packagingItemDraft')
          return (
            <span className='inline-flex' title={label} aria-label={label}>
              <Clock3
                className='size-4 text-amber-600 dark:text-amber-500'
                aria-hidden
              />
            </span>
          )
        },
      },
      {
        id: 'complianceRedFlags',
        accessorFn: (row) => row.complianceRedFlags.length,
        getUniqueValues: (row) => row.complianceRedFlags,
        filterFn: (row, _columnId, filterValue) => {
          const selectedValues = Array.isArray(filterValue) ? filterValue : []
          if (selectedValues.length === 0) {
            return true
          }
          return row.original.complianceRedFlags.some((flag) =>
            selectedValues.includes(flag)
          )
        },
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('list.complianceRedFlags.column')}
          />
        ),
        enableHiding: false,
        cell: ({ row }) => (
          <PackagingItemComplianceRedFlagsCell
            complianceRedFlags={row.original.complianceRedFlags}
          />
        ),
      },
      {
        id: 'audit',
        accessorFn: (row) => row.auditStatus,
        filterFn: (row, id, value) => {
          const selectedValues = Array.isArray(value) ? value : []
          if (selectedValues.length === 0) {
            return true
          }
          return selectedValues.includes(String(row.getValue(id)))
        },
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('list.columnAudit')}
          />
        ),
        enableHiding: false,
        cell: ({ row }) => (
          <PackagingItemAuditCell
            auditStatus={row.original.auditStatus}
            auditIssues={row.original.auditIssues}
            auditLastRunAt={row.original.auditLastRunAt}
          />
        ),
      },
      {
        id: 'lastUpdatedByUser',
        accessorFn: (row) =>
          packagingItemTimestampSortValue(row.lastChangeHistoryAtBySupplier),
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('packagingItems.columnLastUpdatedByUser')}
          />
        ),
        enableHiding: false,
        sortUndefined: 'last',
        cell: ({ row }) => (
          <PackagingItemChangeHistoryTimestampCell
            timestamp={row.original.lastChangeHistoryAtBySupplier}
          />
        ),
      },
      {
        id: 'lastUpdatedByLex',
        accessorFn: (row) =>
          packagingItemTimestampSortValue(row.lastChangeHistoryAtByLex),
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('packagingItems.columnLastUpdatedByLex')}
          />
        ),
        enableHiding: false,
        sortUndefined: 'last',
        cell: ({ row }) => (
          <PackagingItemChangeHistoryTimestampCell
            timestamp={row.original.lastChangeHistoryAtByLex}
          />
        ),
      },
      {
        id: 'heavyMetalsAudit',
        accessorFn: (row) => row.heavyMetalsAuditStatus,
        filterFn: (row, _id, value) => {
          const selectedValues = Array.isArray(value)
            ? value.map((entry) => String(entry))
            : []
          return packagingItemMatchesHeavyMetalsAuditFilterValue(
            {
              heavyMetalsAuditStatus: row.original.heavyMetalsAuditStatus,
              heavyMetalsAuditLatest: row.original.heavyMetalsAuditLatest,
            },
            selectedValues
          )
        },
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('list.columnHeavyMetalsAudit')}
          />
        ),
        enableHiding: false,
        cell: ({ row }) => (
          <PackagingItemHeavyMetalsAuditCell
            heavyMetalsAuditStatus={row.original.heavyMetalsAuditStatus}
            heavyMetalsAuditLatest={row.original.heavyMetalsAuditLatest}
            heavyMetalsAuditLastRunAt={row.original.heavyMetalsAuditLastRunAt}
          />
        ),
      },
      {
        id: 'svhcAudit',
        accessorFn: (row) => row.svhcAuditStatus,
        filterFn: (row, _id, value) => {
          const selectedValues = Array.isArray(value)
            ? value.map((entry) => String(entry))
            : []
          return packagingItemMatchesSvhcAuditFilterValue(
            {
              svhcAuditStatus: row.original.svhcAuditStatus,
              svhcAuditLatest: row.original.svhcAuditLatest,
            },
            selectedValues
          )
        },
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('list.columnSvhcAudit')}
          />
        ),
        enableHiding: false,
        cell: ({ row }) => (
          <PackagingItemSvhcAuditCell
            svhcAuditStatus={row.original.svhcAuditStatus}
            svhcAuditLatest={row.original.svhcAuditLatest}
            svhcAuditLastRunAt={row.original.svhcAuditLastRunAt}
          />
        ),
      },
    ],
    [isArtemAdmin, t]
  )

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter: searchQuery,
      columnFilters,
      pagination,
    },
    onSortingChange,
    onGlobalFilterChange: setSearchQuery,
    onColumnFiltersChange,
    onPaginationChange,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    pageCount: pageInfo.totalPages,
    globalFilterFn: () => true,
    getCoreRowModel: getCoreRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  })

  const complianceRedFlagsToolbarFilter = useMemo(
    () => ({
      columnId: 'complianceRedFlags',
      title: t('list.complianceRedFlags.column'),
      options: PACKAGING_ITEM_COMPLIANCE_RED_FLAG_VALUES.map((value) => ({
        value,
        label: t(redFlagLabelKey(value)),
        icon: CircleAlert,
      })),
    }),
    [t]
  )

  const auditToolbarFilter = useMemo(
    () => ({
      columnId: 'audit',
      title: t('list.columnAudit'),
      options: PACKAGING_ITEM_AUDIT_STATUS_VALUES.map((value) => {
        let icon = CircleDashed
        if (value === 'clear') {
          icon = CircleCheck
        }
        if (value === 'flagged') {
          icon = CircleAlert
        }
        return {
          value,
          label: t(packagingItemAuditStatusLabelKey(value)),
          icon,
        }
      }),
    }),
    [t]
  )

  const heavyMetalsAuditToolbarFilter = useMemo(
    () => ({
      columnId: 'heavyMetalsAudit',
      title: t('list.columnHeavyMetalsAudit'),
      showCounts: false,
      options: [
        ...PACKAGING_ITEMS_OVERVIEW_HEAVY_METALS_AUDIT_STATUS_FILTER_VALUES.map(
          (value) => {
            let icon = CircleDashed
            if (value === 'passed') {
              icon = CircleCheck
            }
            if (value === 'failed') {
              icon = CircleAlert
            }
            if (value === 'flagged') {
              icon = TriangleAlert
            }
            return {
              type: 'option' as const,
              value,
              label: t(packagingItemHeavyMetalsAuditStatusLabelKey(value)),
              icon,
            }
          }
        ),
        { type: 'separator' as const },
        ...[...PACKAGING_ITEMS_OVERVIEW_HEAVY_METALS_AUDIT_VERDICT_FILTER_VALUES]
          .sort(compareHeavyMetalsAuditVerdictFilterValues)
          .map((value) => ({
            type: 'option' as const,
            value,
            label: t(packagingHeavyMetalsAuditVerdictLabelKey(value)),
          })),
      ],
    }),
    [t]
  )

  const svhcAuditToolbarFilter = useMemo(
    () => ({
      columnId: 'svhcAudit',
      title: t('list.columnSvhcAudit'),
      showCounts: false,
      options: [
        ...PACKAGING_ITEMS_OVERVIEW_SVHC_AUDIT_STATUS_FILTER_VALUES.map(
          (value) => {
            let icon = CircleDashed
            if (value === 'passed') {
              icon = CircleCheck
            }
            if (value === 'failed') {
              icon = CircleAlert
            }
            if (value === 'flagged') {
              icon = TriangleAlert
            }
            return {
              type: 'option' as const,
              value,
              label: t(packagingItemSvhcAuditStatusLabelKey(value)),
              icon,
            }
          }
        ),
        { type: 'separator' as const },
        ...[...PACKAGING_ITEMS_OVERVIEW_SVHC_AUDIT_VERDICT_FILTER_VALUES]
          .sort(compareSvhcAuditVerdictFilterValues)
          .map((value) => ({
            type: 'option' as const,
            value,
            label: t(packagingSvhcAuditVerdictLabelKey(value)),
          })),
      ],
    }),
    [t]
  )

  useEffect(() => {
    ensurePageInRange(pageInfo.totalPages)
  }, [ensurePageInRange, pageInfo.totalPages])

  const hasActiveFilters =
    columnFilters.length > 0 ||
    lastUpdatedFilter != null ||
    Boolean(table.getState().globalFilter)

  const exportFileName = useMemo(
    () =>
      buildPackagingExportFilename({
        date: new Date(),
        organizationName: organization?.name,
        kind: 'packaging-items',
      }),
    [organization?.name]
  )

  return (
    <div
      className={cn(
        'max-sm:has-[div[role="toolbar"]]:mb-16',
        'flex min-h-0 flex-1 flex-col gap-4'
      )}
    >
      <div className='flex shrink-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
        <div className='min-w-0 flex-1'>
          <DataTableToolbar
            table={table}
            searchPlaceholder={t('packagingItems.searchPlaceholder')}
            showViewOptions={false}
            showFilterCounts={false}
            filters={[
              complianceRedFlagsToolbarFilter,
              auditToolbarFilter,
              heavyMetalsAuditToolbarFilter,
              svhcAuditToolbarFilter,
            ]}
            filterActions={
              <>
                <PackagingItemsSupplierFilterCombobox
                  id='packaging-items-supplier-filter'
                  className='h-8 w-auto max-w-[220px]'
                  value={supplierIdFromUrl === '' ? null : supplierIdFromUrl}
                  selectedLabel={selectedSupplierName}
                  onValueChange={handleSupplierFilterChange}
                  placeholder={t('packagingItems.supplierFilterPlaceholder')}
                  searchPlaceholder={t(
                    'packagingItems.supplierFilterSearchPlaceholder'
                  )}
                  emptyText={t('packagingItems.supplierFilterEmpty')}
                  clearLabel={t('packagingItems.supplierFilterClear')}
                />
                <PackagingItemsLastUpdatedFacetedFilter
                  value={lastUpdatedFilter}
                  onValueChange={handleLastUpdatedFilterChange}
                />
              </>
            }
            actions={
              <ExportXlsxButton
                download={async () => {
                  await downloadPackagingItemsOverviewExport(
                    exportQuery,
                    exportFileName
                  )
                }}
                hasActiveFilters={hasActiveFilters}
              />
            }
          />
        </div>
      </div>

      <div
        className='flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border'
        aria-busy={isFetching}
      >
        <div className='min-h-0 flex-1 overflow-auto'>
          <table className='w-full min-w-7xl table-fixed caption-bottom text-sm'>
            <PackagingItemsTableColGroup />
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className='group/row'>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className='sticky top-0 z-10 bg-background group-hover/row:bg-muted'
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className='group/row'>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={
                          cell.column.id === 'audit' ||
                          cell.column.id === 'complianceRedFlags'
                            ? 'bg-background group-hover/row:bg-muted'
                            : 'max-w-0 truncate overflow-hidden bg-background group-hover/row:bg-muted'
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className='h-24 text-center text-muted-foreground'
                  >
                    {t('packagingItems.notFound')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </table>
        </div>
        <DataTablePagination
          table={table}
          className='shrink-0 border-t bg-background py-3'
        />
      </div>
    </div>
  )
}
